<template>
    <a-modal
        v-model="modalVisible"
        :title="modalTitle"
        :footer="null"
        width="520px"
        @cancel="handleCancel"
    >
        <a-form layout="vertical">
            <!-- 待办标题 -->
            <a-form-item label="待办标题" required>
                <a-input v-if="isEditable" v-model="formData.title" placeholder="请输入待办标题" />
                <div v-else class="readonly-text">{{ formData.title }}</div>
            </a-form-item>

            <!-- 待办时间 切换单选 -->
            <a-form-item label="待办时间">
                <template v-if="isEditable">
                    <a-radio-group v-model="formData.timeType">
                        <a-radio-button value="point">时间点</a-radio-button>
                        <a-radio-button value="range">时间段</a-radio-button>
                    </a-radio-group>
                </template>
                <div v-else class="readonly-text">{{ formData.timeType === 'point' ? '时间点' : '时间段' }}</div>
            </a-form-item>

            <!-- 模式1：时间点 -->
            <div v-if="formData.timeType === 'point'" class="time-point-row">
                <a-form-item>
                    <a-space size="middle">
                        <template v-if="isEditable">
                            <a-date-picker v-model="formData.pointDate" format="YYYY-MM-DD" style="width: 180px" />
                            <a-time-picker v-model="formData.pointTime" format="HH:mm" style="width: 140px" />
                        </template>
                        <template v-else>
                            <span class="readonly-text">{{ pointDateTimeStr }}</span>
                        </template>
                    </a-space>
                </a-form-item>
            </div>

            <!-- 模式2：时间段 -->
            <div v-else class="time-range-wrap">
                <a-form-item label="开始时间">
                    <a-space size="middle">
                        <template v-if="isEditable">
                            <a-date-picker v-model="formData.startDate" format="YYYY-MM-DD" style="width: 180px" />
                            <a-time-picker v-model="formData.startTime" format="HH:mm" style="width: 140px" />
                        </template>
                        <template v-else>
                            <span class="readonly-text">{{ startDateTimeStr }}</span>
                        </template>
                    </a-space>
                </a-form-item>

                <a-form-item label="结束时间">
                    <a-space size="middle">
                        <template v-if="isEditable">
                            <a-date-picker v-model="formData.endDate" format="YYYY-MM-DD" style="width: 180px" />
                            <a-time-picker v-model="formData.endTime" format="HH:mm" style="width: 140px" />
                        </template>
                        <template v-else>
                            <span class="readonly-text">{{ endDateTimeStr }}</span>
                        </template>
                    </a-space>
                </a-form-item>
            </div>

            <!-- 待办内容 -->
            <a-form-item label="待办内容">
                <a-textarea v-if="isEditable" v-model="formData.remark" placeholder="请输入待办内容描述..." :rows="5" />
                <div v-else class="readonly-text textarea-readonly">{{ formData.remark || '-' }}</div>
            </a-form-item>

            <!-- 待办详情：修改/删除按钮 -->
            <a-form-item v-if="isDetailMode && detailType === 'todo_list' && !isEditable" class="form-btn-wrap">
                <a-button type="primary" @click="enterEdit">修改</a-button>
                <a-button style="margin-left: 10px;" type="danger" @click="handleDelete">删除</a-button>
            </a-form-item>

            <!-- 会议详情只显示关闭按钮 -->
            <a-form-item v-else-if="isDetailMode && detailType === 'meeting' && !isEditable" class="form-btn-wrap">
                <a-button @click="handleCancel">关闭</a-button>
            </a-form-item>

            <!-- 其他详情兜底：只显示关闭 -->
            <a-form-item v-else-if="isDetailMode && !isEditable" class="form-btn-wrap">
                <a-button @click="handleCancel">关闭</a-button>
            </a-form-item>

            <!-- 编辑模式：确认/取消按钮 -->
            <a-form-item v-else-if="isEditable" class="form-btn-wrap">
                <a-button @click="exitEdit" v-if="isDetailMode">取消</a-button>
                <a-button @click="handleCancel" v-else>取消</a-button>
                <a-button style="margin-left: 10px;" type="primary" @click="handleSubmit">{{ isDetailMode ? '确认修改' : '确认新增' }}</a-button>
            </a-form-item>
        </a-form>
    </a-modal>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'
import { todoListAdd, todoListDelete, todoListUpdate } from '@/api/approval/todo'

export default {
    name: 'AddTodo',
    data() {
        return {
            modalVisible: false,
            isEditable: false,
            isDetailMode: false,
            detailType: '',
            currentRecord: null,
            formData: this.createDefaultForm()
        }
    },
    computed: {
        ...mapState('auth', ['user']),
        modalTitle() {
            if (this.isDetailMode) {
                const typeMap = { meeting: '会议详情', todo_list: '待办详情' }
                if (this.isEditable) return '修改待办'
                return typeMap[this.detailType] || '待办详情'
            }
            return '新增待办'
        },
        pointDateTimeStr() {
            const d = this.formData.pointDate ? moment(this.formData.pointDate).format('YYYY-MM-DD') : '-'
            const t = this.formData.pointTime ? moment(this.formData.pointTime).format('HH:mm') : ''
            return d + (t ? ' ' + t : '')
        },
        startDateTimeStr() {
            const d = this.formData.startDate ? moment(this.formData.startDate).format('YYYY-MM-DD') : '-'
            const t = this.formData.startTime ? moment(this.formData.startTime).format('HH:mm') : ''
            return d + (t ? ' ' + t : '')
        },
        endDateTimeStr() {
            const d = this.formData.endDate ? moment(this.formData.endDate).format('YYYY-MM-DD') : '-'
            const t = this.formData.endTime ? moment(this.formData.endTime).format('HH:mm') : ''
            return d + (t ? ' ' + t : '')
        }
    },
    methods: {
        createDefaultForm() {
            const defaultDay = moment()
            return {
                id: null,
                title: '',
                timeType: 'point',
                pointDate: defaultDay,
                pointTime: moment('00:00', 'HH:mm'),
                startDate: defaultDay,
                startTime: moment('00:00', 'HH:mm'),
                endDate: defaultDay,
                endTime: moment('00:00', 'HH:mm'),
                content: '',
                remark: ''
            }
        },
        openModal(date) {
            this.isDetailMode = false
            this.isEditable = true
            this.currentRecord = null
            if (date) {
                const day = moment(date).format('YYYY-MM-DD')
                this.formData.pointDate = moment(day)
                this.formData.startDate = moment(day)
                this.formData.endDate = moment(day)
            }
            this.modalVisible = true
        },
        openDetail(record, type) {
            this.isDetailMode = true
            this.isEditable = false
            this.detailType = type || ''
            this.currentRecord = record
            const startStr = record.startTime || record.start || ''
            const endStr = record.endTime || record.end || ''
            const startMoment = startStr ? moment(startStr) : moment()
            const endMoment = endStr ? moment(endStr) : null
            const hasEndTime = endStr && endMoment && endMoment.isValid()
            this.formData = {
                id: record.id || record.todoListId || null,
                title: record.title || record.content || '',
                timeType: hasEndTime ? 'range' : 'point',
                pointDate: startMoment,
                pointTime: startMoment,
                startDate: startMoment,
                startTime: startMoment,
                endDate: hasEndTime ? endMoment : moment(),
                endTime: hasEndTime ? endMoment : moment('00:00', 'HH:mm'),
                content: record.content || record.remark || '',
                remark: record.remark || ''
            }
            this.modalVisible = true
        },
        handleCancel() {
            this.modalVisible = false
            this.isDetailMode = false
            this.isEditable = false
            this.detailType = ''
            this.currentRecord = null
            this.formData = this.createDefaultForm()
        },
        enterEdit() {
            this.isEditable = true
        },
        exitEdit() {
            if (this.currentRecord) {
                const startStr = this.currentRecord.startTime || this.currentRecord.start || ''
                const endStr = this.currentRecord.endTime || this.currentRecord.end || ''
                const startMoment = startStr ? moment(startStr) : moment()
                const endMoment = endStr ? moment(endStr) : null
                const hasEndTime = endStr && endMoment && endMoment.isValid()
                this.formData = {
                    id: this.currentRecord.id || this.currentRecord.todoListId || null,
                    title: this.currentRecord.title || this.currentRecord.content || '',
                    timeType: hasEndTime ? 'range' : 'point',
                    pointDate: startMoment,
                    pointTime: startMoment,
                    startDate: startMoment,
                    startTime: startMoment,
                    endDate: hasEndTime ? endMoment : moment(),
                    endTime: hasEndTime ? endMoment : moment('00:00', 'HH:mm'),
                    content: this.currentRecord.content || this.currentRecord.remark || '',
                    remark: this.currentRecord.remark || ''
                }
            }
            this.isEditable = false
        },
        handleDelete() {
            const that = this
            this.$confirm({
                title: '确认删除',
                content: '确定要删除这条待办事项吗？',
                onOk() {
                    const todoId = that.formData.id || that.currentRecord.id || that.currentRecord.todoListId
                    todoListDelete({ todoId }).then(() => {
                        // that.$message.success('删除成功')
                        that.handleCancel()
                        that.$emit('success')
                    }).catch(() => {
                        that.$message.error('删除失败')
                    })
                }
            })
        },
        handleSubmit() {
            if (!this.formData.title.trim()) {
                this.$message.error('请输入待办标题')
                return
            }
            const submitParams = {
                title: this.formData.title,
                content: this.formData.remark,
                createBy: this.user ? this.user.uuid : '',
                startTime: this.formData.timeType === 'point'
                    ? moment(this.formData.pointDate).format('YYYY-MM-DD') + ' ' + moment(this.formData.pointTime).format('HH:mm:ss')
                    : moment(this.formData.startDate).format('YYYY-MM-DD') + ' ' + moment(this.formData.startTime).format('HH:mm:ss'),
                endTime: this.formData.timeType === 'point'
                    ? null
                    : moment(this.formData.endDate).format('YYYY-MM-DD') + ' ' + moment(this.formData.endTime).format('HH:mm:ss')
            }

            if (this.isDetailMode && this.currentRecord) {
                submitParams.id = this.currentRecord.id || this.currentRecord.todoListId
                todoListUpdate(submitParams).then(() => {
                    // this.$message.success('修改成功')
                    this.handleCancel()
                    this.$emit('success')
                }).catch(() => {
                    this.$message.error('修改失败')
                })
            } else {
                todoListAdd(submitParams).then(() => {
                    // this.$message.success('新增成功')
                    this.handleCancel()
                    this.$emit('success')
                }).catch(() => {
                    this.$message.error('新增失败')
                })
            }
        }
    }
}
</script>

<style scoped>
.time-point-row :deep(.ant-form-item) {
    margin-bottom: 0;
}
.time-range-wrap :deep(.ant-form-item) {
    margin-bottom: 12px;
}
.form-btn-wrap {
    margin-bottom: 0 !important;
    text-align: right;
}
.readonly-text {
    padding: 6px 0;
    color: #333;
    font-size: 14px;
    line-height: 1.6;
    word-break: break-all;
}
.textarea-readonly {
    white-space: pre-wrap;
    min-height: 60px;
}
</style>
